import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Typography, Avatar, IconButton, Backdrop,
  CircularProgress, Dialog, DialogContent, Button, Slider
} from '@mui/material';
import { Delete, ArrowBackIos, ArrowForwardIos, Close } from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import { getUserId } from '../utils/auth';
import api from '../utils/api';
import { useAuth } from '../auth/AuthContext';
import { Area } from 'react-easy-crop';
import { useSwipeable } from 'react-swipeable';
import { Container, Paper } from '@mui/material';
import { BASE_URL } from '../config';

const MAX_PHOTOS = 5;
type PhotoSizeType = 'THUMB' | 'SMALL' | 'MEDIUM' | 'LARGE' | 'ORIGINAL';

function getCroppedImg(imageSrc: string, crop: any) {
  return new Promise<string>((resolve) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          image,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height
        );
        resolve(canvas.toDataURL('image/jpeg'));
      }
    };
  });
}

const EditPhotosPage = () => {
  const userId = getUserId();
  const { userInfo, refreshUserInfo } = useAuth();

  const [photos, setPhotos] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [openViewer, setOpenViewer] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [loadingViewer, setLoadingViewer] = useState<boolean>(false);

  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoomCrop, setZoomCrop] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePhotoId, setDeletePhotoId] = useState<string | null>(null);

  const profilePhoto = userInfo?.profilePhoto || null;

  const uploadOriginal = async () => {
    if (!cropImageSrc) return;
    setLoading(true);
    const blob = await (await fetch(cropImageSrc)).blob();
    const file = new File([blob], 'original.jpg', { type: 'image/jpeg' });
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/object-store/img/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCropDialogOpen(false);
      setCropImageSrc(null);
      await updateState();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => nextImage(),
    onSwipedRight: () => prevImage(),
    trackMouse: true,
  });

  const handleDeleteConfirm = (photoId: string) => {
    setDeletePhotoId(photoId);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePhoto = async () => {
    if (!deletePhotoId) return;
    try {
      setLoading(true);
      await api.delete(`/object-store/img/delete/${deletePhotoId}`);
      await updateState();
      setOpenViewer(false);
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeletePhotoId(null);
    }
  };

  useEffect(() => {
    if (userInfo?.photos) {
      const sorted = [...userInfo.photos];
      const profileIndex = sorted.findIndex(p => p.split('/').pop() === userInfo.profilePhoto);
      if (profileIndex > -1) {
        const [profile] = sorted.splice(profileIndex, 1);
        sorted.unshift(profile);
      }
      setPhotos(sorted);
    }
  }, [userInfo]);

  const updateState = async () => {
    setLoading(true);
    await refreshUserInfo?.();
    setLoading(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    if (photos.length >= MAX_PHOTOS) {
      alert(`You can upload max ${MAX_PHOTOS} photo.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = useCallback(
    (croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    []
  );

  const uploadCropped = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;
    setLoading(true);
    const croppedDataUrl = await getCroppedImg(cropImageSrc, croppedAreaPixels);
    const blob = await (await fetch(croppedDataUrl)).blob();
    const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/object-store/img/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCropDialogOpen(false);
      setCropImageSrc(null);
      await updateState();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };



  const handleSetProfile = async (photoId: string) => {
    try {
      setLoading(true);
      await api.get(`/api/user/set/profile/photo/${photoId}`);
      await updateState();
      setOpenViewer(false);
    } catch (error) {
      console.error('Set as profile photo error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openImageViewer = (index: number) => {
    setCurrentIndex(index);
    setOpenViewer(true);
    setZoom(1);
    setLoadingViewer(true);
  };

  const closeImageViewer = () => {
    setOpenViewer(false);
    setZoom(1);
    setLoadingViewer(false);
  };

  const nextImage = () => {

    if (photos.length > 1) {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
      setZoom(1);
      setLoadingViewer(true);
    }
  };

  const prevImage = () => {
    if (photos.length > 1) {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    setZoom(1);
    setLoadingViewer(true);
    }
  };

  const getImageUrl = (index: number, size: PhotoSizeType = 'SMALL') => {
    const path = photos[index];
    const fileName = path?.split('/')?.pop();
    return fileName ? `${BASE_URL}/object-store/img/get/${userId}/${size}/${fileName}` : '';
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!openViewer) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openViewer]);

  return (
    <Container maxWidth="md" sx={{ mt: 1, mb: 6 }}>
      <Paper elevation={8} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
        <Box sx={{ mt: 4, px: 2 }}>
          <Typography variant="h5" gutterBottom align="center">Edit Photos</Typography>
          <Grid container spacing={2} justifyContent="center">
            {photos.map((photo, index) => (
              <Grid key={photo}  size={{ xs: 6, sm: 4, md: 3 }}>
                <Box onClick={() => openImageViewer(index)} sx={{
                  position: 'relative', width: '100%',
                  aspectRatio: '1', borderRadius: 2, overflow: 'hidden',
                  boxShadow: 3, cursor: 'pointer',
                  '&:hover': { transform: 'scale(1.03)' },
                }}>
                  <Avatar variant="square" src={getImageUrl(index, 'MEDIUM')} sx={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    objectFit: 'cover',
                  }} />
                </Box>
              </Grid>
            ))}
            {photos.length < MAX_PHOTOS && (
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <label htmlFor="upload-photo">
                  <input
                    style={{ display: 'none' }}
                    id="upload-photo"
                    name="upload-photo"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    pb: '100%', // padding-bottom %100 = kare oranını korur
                    backgroundColor: '#f5f5f5',
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'clamp(24px, 10vw, 48px)', // responsive font boyutu
                      fontWeight: 'bold',
                      color: '#888',
                    }}>
                      +
                    </Box>
                  </Box>
                </label>
              </Grid>
            )}
          </Grid>

          {/* Crop Dialog */}
          <Dialog
            open={cropDialogOpen}
            onClose={() => setCropDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: 600,
                height: '90vh',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box position="relative" width="100%" flex={1} minHeight={200}>
                {cropImageSrc && (
                  <Cropper
                    image={cropImageSrc}
                    crop={crop}
                    zoom={zoomCrop}
                    onCropChange={setCrop}
                    onZoomChange={setZoomCrop}
                    onCropComplete={handleCropComplete}
                  />
                )}
              </Box>

              <Box mt={2}>
                <Slider
                  value={zoomCrop}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(_, v) => setZoomCrop(v as number)}
                />
              </Box>

              <Box mt={2} display="flex" justifyContent="flex-end" gap={2} flexWrap="wrap">
                <Button onClick={() => setCropDialogOpen(false)}>Cancel</Button>
                <Button onClick={uploadOriginal} variant="outlined">Upload Original</Button>
                <Button onClick={uploadCropped} variant="contained">Upload</Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Image Viewer */}
          <Dialog open={openViewer} onClose={closeImageViewer} fullScreen>
            <DialogContent
              {...handlers}
              onWheel={(e) => {
                e.preventDefault();
                setZoom((z) => Math.min(Math.max(z - e.deltaY * 0.001, 1), 3));
              }}
              sx={{
                backgroundColor: 'black',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0,
                touchAction: 'pan-y',
              }}
            >
              <IconButton onClick={closeImageViewer} sx={{ position: 'absolute', top: 10, right: 10, color: 'white' }}>
                <Close fontSize="large" />
              </IconButton>

              <Backdrop open={loadingViewer} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
              </Backdrop>
              <Box
                component="img"
                src={getImageUrl(currentIndex, 'LARGE')}
                alt=""
                onLoad={() => setLoadingViewer(false)}
                sx={{
                  maxHeight: '80%', maxWidth: '90%', objectFit: 'contain',
                  transform: `scale(${zoom})`, transition: 'transform 0.3s ease',
                }}
              />
              <Typography variant="body2" color="white" sx={{ mt: 2 }}>
                {currentIndex + 1} / {photos.length}
              </Typography>
              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                {profilePhoto !== (photos[currentIndex]?.split('/').pop() ?? '') && (
                  <>
                    <Button
                      onClick={() => handleDeleteConfirm(photos[currentIndex]?.split('/').pop() ?? '')}
                      color="error"
                      variant="contained"
                      startIcon={<Delete />}
                    >
                      Delete
                    </Button>
                    <Button
                      onClick={() => handleSetProfile(photos[currentIndex]?.split('/').pop() ?? '')}
                      variant="contained"
                    >
                      Set As Profile Photo
                    </Button>
                  </>
                )}

                {photos.length > 1 && (
                  <>
                    <IconButton
                      onClick={prevImage}
                      sx={{ position: 'absolute', top: '50%', left: 10, color: 'white' }}
                    >
                      <ArrowBackIos fontSize="large" />
                    </IconButton>
                    <IconButton
                      onClick={nextImage}
                      sx={{ position: 'absolute', top: '50%', right: 10, color: 'white' }}
                    >
                      <ArrowForwardIos fontSize="large" />
                    </IconButton>
                  </>
                )}
              </Box>
            </DialogContent>
          </Dialog>
          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            {/* Confirm Dialog */}
            <DialogContent>
              <Typography variant="h6" gutterBottom>
                Are you sure you want to delete this photo?
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button onClick={confirmDeletePhoto} color="error" variant="contained">
                  Delete
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          {/* Global Loading */}
          <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 2 }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditPhotosPage;